import { Suspense, lazy } from 'react'
import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router'

import { RootLayout } from './pages/RootLayout'
import { InboxPage } from './pages/InboxPage'
import ClientsPage from './pages/ClientsPage'
import ClientDetailPage from './pages/ClientDetailPage'
import ChannelsPage from './pages/ChannelsPage'
import ChannelDetailPage from './pages/ChannelDetailPage'
import WorkflowsPage from './pages/WorkflowsPage'
import WorkflowDetailPage from './pages/WorkflowDetailPage'
import TransactionsPage from './pages/TransactionsPage'
import ApiDocsPage from './pages/ApiDocsPage'
import SystemHealthPage from './pages/SystemHealthPage'

// Dashboard pulls recharts — keep it in its own chunk.
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
// Audit Log pulls recharts too — keep it in its own chunk.
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'))

// Root carries no chrome — the back-office shell lives in a pathless layout
// route so standalone pages (/inbox for the APK WebView) can opt out.
const rootRoute = createRootRoute({})

const portalShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'portal',
  component: RootLayout,
})

const indexRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' })
  },
})

const dashboardRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'dashboard',
  component: () => (
    <Suspense fallback={<div className="panel muted">กำลังโหลด dashboard…</div>}>
      <DashboardPage />
    </Suspense>
  ),
})

const auditRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'audit',
  component: () => (
    <Suspense fallback={<div className="panel muted">กำลังโหลด Audit Log…</div>}>
      <AuditLogPage />
    </Suspense>
  ),
})

const clientsRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'clients',
  component: ClientsPage,
})

const clientDetailRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'clients/$clientId',
  component: ClientDetailPage,
})

const workflowsRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'workflows',
  component: WorkflowsPage,
})

const workflowDetailRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'workflows/$workflowKey',
  component: WorkflowDetailPage,
})

const channelsRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'channels',
  component: ChannelsPage,
})

const channelDetailRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'channels/$channelId',
  component: ChannelDetailPage,
})

const transactionsRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'transactions',
  component: TransactionsPage,
})

const apiDocsRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'api-docs',
  component: ApiDocsPage,
})

const healthRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'health',
  component: SystemHealthPage,
})

// ── Legacy URL mapping (/services/$slug/<tab> → new IA) ──────────────────────
const TAB_REDIRECTS: Record<string, string> = {
  health: '/health',
  dashboard: '/dashboard',
  workflows: '/workflows',
  transactions: '/transactions',
  simulator: '/transactions',
  'api-spec': '/api-docs',
  docs: '/api-docs',
}

const legacyServiceRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'services/$slug',
  beforeLoad: () => {
    throw redirect({ to: '/health' })
  },
})

const legacyServiceTabRoute = createRoute({
  getParentRoute: () => portalShellRoute,
  path: 'services/$slug/$tab',
  beforeLoad: ({ params }) => {
    throw redirect({ to: TAB_REDIRECTS[params.tab] ?? '/dashboard' })
  },
})

// Standalone inbox for the APK WebView — hangs off the root (no portal
// chrome). URL contract: /inbox?subscriber=CIF-xxxx
const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'inbox',
  validateSearch: (search: Record<string, unknown>): { subscriber?: string } => ({
    subscriber: typeof search.subscriber === 'string' ? search.subscriber : undefined,
  }),
  component: InboxPage,
})

const routeTree = rootRoute.addChildren([
  inboxRoute,
  portalShellRoute.addChildren([
    indexRoute,
    dashboardRoute,
    auditRoute,
    clientsRoute,
    clientDetailRoute,
    workflowsRoute,
    workflowDetailRoute,
    channelsRoute,
    channelDetailRoute,
    transactionsRoute,
    apiDocsRoute,
    healthRoute,
    legacyServiceRoute,
    legacyServiceTabRoute,
  ]),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
